/**
 * CatererMenuCard — variant-style caterer menu builder (Feb 2026).
 *
 * For each meal (Tiffin / Lunch / Snacks / Dinner), the caterer can create
 * up to 10 NAMED VARIANTS — e.g.
 *   Tiffin → "Standard Tiffin ₹120", "Premium Tiffin ₹180", "Mini Tiffin ₹90"
 *   Lunch  → "Veg Meals ₹250", "Non-Veg Combo ₹400"
 * Each variant holds:
 *   - name (required, ≤60 chars)
 *   - price_per_plate (₹)
 *   - dish list (just names, Quick-Add autocomplete from CATERER_DISH_LIBRARY)
 *
 * OCR scan: scanning a menu photo creates one new variant per meal that has
 * detected dishes (named "Scanned Tiffin", etc.) so vendor never loses an
 * existing variant.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Camera, Trash, Plus, FloppyDisk, Sparkle, Warning, Lightning,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { CATERER_DISH_LIBRARY, MEAL_OPTIONS } from '../data/catererDishLibrary';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MEAL_KEYS = MEAL_OPTIONS.map((m) => m.key);
const MAX_VARIANTS_PER_MEAL = 10;

const TONE_BG = {
  amber: 'bg-amber-50 border-amber-200 text-amber-800',
  orange: 'bg-orange-50 border-orange-200 text-orange-800',
  pink: 'bg-pink-50 border-pink-200 text-pink-800',
  indigo: 'bg-indigo-50 border-indigo-200 text-indigo-800',
};

const blankVariant = (meal, idx) => ({
  name: `${MEAL_OPTIONS.find((m) => m.key === meal)?.label || 'Menu'} ${idx + 1}`,
  price_per_plate: '',
  dishes: [],
});

const emptyVariants = () => ({ tiffin: [], lunch: [], snacks: [], dinner: [] });

const DishNameInput = ({ value, meal, onChange, testId, autoFocus = false }) => {
  /**
   * Dish-name <Input> with Quick-Add autocomplete dropdown sourced from
   * CATERER_DISH_LIBRARY. Suggestions for the row's current meal show first.
   */
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const handleOut = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleOut);
    return () => document.removeEventListener('mousedown', handleOut);
  }, []);

  const matches = useMemo(() => {
    const q = (value || '').toLowerCase().trim();
    if (q.length < 1) {
      return CATERER_DISH_LIBRARY.filter((d) => d.meal === meal).slice(0, 8);
    }
    const own = [];
    const other = [];
    for (const d of CATERER_DISH_LIBRARY) {
      if (d.name.toLowerCase().includes(q)) {
        if (d.meal === meal) own.push(d);
        else other.push(d);
      }
    }
    return [...own, ...other].slice(0, 10);
  }, [value, meal]);

  return (
    <div className="relative col-span-11" ref={wrapRef}>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setOpen(true)}
        autoFocus={autoFocus}
        placeholder="Type or pick — e.g. Sambar Rice"
        className="h-9 rounded-none border-[#E8E1D5]"
        data-testid={testId}
      />
      {open && matches.length > 0 && (
        <div className="absolute z-30 left-0 right-0 top-full mt-0.5 max-h-60 overflow-y-auto bg-white border border-[#E8E1D5] shadow-lg rounded-none"
          data-testid={`${testId}-suggestions`}>
          <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-text-secondary px-2 py-1 bg-sand-50 border-b border-[#E8E1D5]">
            <Lightning size={10} weight="fill" className="text-brand-primary" />
            Quick Add — click to fill
          </div>
          {matches.map((d) => (
            <button
              key={`${d.meal}-${d.name}`}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); onChange(d.name); setOpen(false); }}
              className={`w-full text-left px-2 py-1.5 text-xs hover:bg-sand-50 flex items-center justify-between gap-2 ${
                d.meal !== meal ? 'opacity-70' : ''
              }`}
              data-testid={`${testId}-suggest-${d.name.replace(/\s+/g, '-').toLowerCase()}`}
            >
              <span>{d.name}</span>
              <span className="text-[9px] uppercase tracking-wider text-text-secondary">
                {d.meal === meal ? d.region : `${d.meal} · ${d.region}`}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const VariantCard = ({
  meal, vIdx, variant, onChange, onDelete,
}) => {
  const updateDish = (di, name) => {
    const next = [...(variant.dishes || [])];
    next[di] = { ...(next[di] || {}), name };
    onChange({ ...variant, dishes: next });
  };
  const addDish = () => {
    onChange({ ...variant, dishes: [...(variant.dishes || []), { name: '' }] });
  };
  const removeDish = (di) => {
    const next = [...(variant.dishes || [])];
    next.splice(di, 1);
    onChange({ ...variant, dishes: next });
  };

  return (
    <div className="bg-white border border-[#E8E1D5] rounded p-3 space-y-2"
      data-testid={`variant-${meal}-${vIdx}`}>
      {/* Variant header */}
      <div className="flex items-start gap-2">
        <Input
          value={variant.name || ''}
          onChange={(e) => onChange({ ...variant, name: e.target.value })}
          placeholder="Menu name (e.g. Premium Tiffin)"
          className="flex-1 h-9 rounded-none border-[#E8E1D5] font-semibold"
          data-testid={`variant-name-${meal}-${vIdx}`}
        />
        <div className="flex items-center gap-1">
          <span className="text-[10px] uppercase tracking-wider font-bold text-text-secondary">₹/plate</span>
          <Input
            type="number" min={0}
            value={variant.price_per_plate ?? ''}
            onChange={(e) => onChange({ ...variant, price_per_plate: e.target.value })}
            placeholder="0"
            className="h-9 w-24 rounded-none border-[#E8E1D5] text-right text-xs"
            data-testid={`variant-price-${meal}-${vIdx}`}
          />
        </div>
        <button type="button" onClick={onDelete}
          className="h-9 px-2 border border-red-200 text-red-500 hover:bg-red-50 rounded-none flex items-center justify-center"
          title="Delete this menu"
          data-testid={`variant-delete-${meal}-${vIdx}`}>
          <Trash size={14} />
        </button>
      </div>

      {/* Dishes */}
      <div className="space-y-1.5 pl-2 border-l-2 border-[#E8E1D5]">
        {(variant.dishes || []).length > 0 && (
          <div className="grid grid-cols-12 gap-2 text-[9px] uppercase tracking-wider font-bold text-text-secondary px-1">
            <div className="col-span-11">Dish</div>
            <div className="col-span-1" />
          </div>
        )}
        {(variant.dishes || []).map((d, di) => (
          <div key={di} className="grid grid-cols-12 gap-2 items-start"
            data-testid={`variant-dish-${meal}-${vIdx}-${di}`}>
            <DishNameInput
              value={d?.name || ''}
              meal={meal}
              onChange={(v) => updateDish(di, v)}
              testId={`variant-dish-name-${meal}-${vIdx}-${di}`}
              autoFocus={di === (variant.dishes.length - 1) && !d?.name}
            />
            <button type="button" onClick={() => removeDish(di)}
              className="col-span-1 h-9 border border-red-200 text-red-500 hover:bg-red-50 rounded-none flex items-center justify-center"
              data-testid={`variant-dish-delete-${meal}-${vIdx}-${di}`}>
              <Trash size={14} />
            </button>
          </div>
        ))}
        <Button
          onClick={addDish}
          variant="outline"
          size="sm"
          className="rounded-none border-dashed border-[#E8E1D5] w-full text-xs h-8"
          data-testid={`variant-add-dish-${meal}-${vIdx}`}>
          <Plus size={14} className="mr-1" /> Add dish
        </Button>
      </div>
    </div>
  );
};

const CatererMenuCard = () => {
  const [variants, setVariants] = useState(emptyVariants());
  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { fetchMenu(); }, []);

  const fetchMenu = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/me/caterer-menu-variants`, { withCredentials: true });
      const v = data.variants || {};
      setVariants({
        tiffin: (v.tiffin || []).map(normaliseVariant),
        lunch: (v.lunch || []).map(normaliseVariant),
        snacks: (v.snacks || []).map(normaliseVariant),
        dinner: (v.dinner || []).map(normaliseVariant),
      });
    } catch { /* fresh empty state */ }
  };

  const updateVariant = (meal, idx, patch) => {
    setVariants((cur) => ({
      ...cur,
      [meal]: cur[meal].map((v, i) => (i === idx ? patch : v)),
    }));
  };

  const deleteVariant = (meal, idx) => {
    setVariants((cur) => ({
      ...cur,
      [meal]: cur[meal].filter((_, i) => i !== idx),
    }));
  };

  const addVariant = (meal) => {
    setVariants((cur) => {
      if ((cur[meal] || []).length >= MAX_VARIANTS_PER_MEAL) {
        toast.error(`Maximum ${MAX_VARIANTS_PER_MEAL} menus per meal`);
        return cur;
      }
      return {
        ...cur,
        [meal]: [...cur[meal], blankVariant(meal, cur[meal].length)],
      };
    });
  };

  const onScan = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error('Image must be under 5 MB'); return; }
    setScanning(true);
    const fd = new FormData();
    fd.append('photo', f);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/me/caterer-menu/scan`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const groups = data.groups || {};
      // Push every detected group's dishes into a NEW variant under that meal.
      // Uncategorized → Tiffin variant (vendor can re-name / move).
      setVariants((cur) => {
        const next = { ...cur };
        const sinkUncategorized = (groups.uncategorized || []);
        MEAL_KEYS.forEach((meal) => {
          const dishes = [...(groups[meal] || [])];
          if (meal === 'tiffin') dishes.push(...sinkUncategorized);
          if (dishes.length === 0) return;
          if ((next[meal] || []).length >= MAX_VARIANTS_PER_MEAL) {
            toast.warning(`${meal}: max ${MAX_VARIANTS_PER_MEAL} menus reached, scan ignored`);
            return;
          }
          const label = MEAL_OPTIONS.find((m) => m.key === meal)?.label || 'Menu';
          next[meal] = [
            ...(next[meal] || []),
            {
              name: `Scanned ${label} ${next[meal].length + 1}`,
              price_per_plate: '',
              dishes: dishes.map((n) => ({ name: n })),
            },
          ];
        });
        return next;
      });
      toast.success(`Found ${data.count || 0} dish${data.count === 1 ? '' : 'es'} — added as new menu(s)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Menu scan failed');
    }
    setScanning(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const save = async () => {
    // Build payload: drop variants with empty names; backend will also clean.
    const payload = { variants: {} };
    MEAL_KEYS.forEach((meal) => {
      payload.variants[meal] = (variants[meal] || [])
        .filter((v) => (v.name || '').trim())
        .map((v) => ({
          name: (v.name || '').trim(),
          price_per_plate: v.price_per_plate === '' || v.price_per_plate == null
            ? null
            : Number(v.price_per_plate),
          dishes: (v.dishes || [])
            .map((d) => ({ name: (d?.name || '').trim() }))
            .filter((d) => d.name),
        }));
    });
    setSaving(true);
    try {
      const { data } = await axios.put(`${BACKEND_URL}/api/me/caterer-menu-variants`,
        payload, { withCredentials: true });
      const v = data.variants || {};
      setVariants({
        tiffin: (v.tiffin || []).map(normaliseVariant),
        lunch: (v.lunch || []).map(normaliseVariant),
        snacks: (v.snacks || []).map(normaliseVariant),
        dinner: (v.dinner || []).map(normaliseVariant),
      });
      const total = MEAL_KEYS.reduce((a, m) => a + (v[m] || []).length, 0);
      toast.success(`Saved ${total} menu${total === 1 ? '' : 's'}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  const totalVariants = MEAL_KEYS.reduce((a, m) => a + (variants[m] || []).length, 0);

  return (
    <Card className="bg-white border-[#E8E1D5] mt-6" data-testid="caterer-menu-card">
      <CardHeader>
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          <Camera size={20} className="text-brand-primary" /> Menus &amp; Pricing
        </CardTitle>
        <CardDescription>
          Create up to <span className="font-semibold">{MAX_VARIANTS_PER_MEAL} menus</span> for each meal
          (Tiffin / Lunch / Snacks / Dinner). Each menu has its own name, <span className="font-semibold">price per plate</span>, and dish list.
          Scan a menu photo with AI to auto-generate a new menu from each meal section detected.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Scan menu CTA */}
        <div className="flex flex-col sm:flex-row gap-2 mb-4">
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp"
            onChange={onScan} className="hidden" data-testid="menu-scan-file" />
          <Button onClick={() => fileRef.current?.click()} disabled={scanning}
            className="flex-1 rounded-none bg-brand-primary hover:bg-[#A84A38] text-sand-50 disabled:opacity-60"
            data-testid="menu-scan-btn">
            <Camera size={16} className="mr-2" weight="fill" />
            {scanning ? 'Scanning menu — please wait…' : 'Scan menu photo with AI'}
          </Button>
        </div>

        {scanning && (
          <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-amber-50 border border-amber-200 text-xs text-amber-800 rounded">
            <Sparkle size={14} weight="fill" className="text-amber-600" />
            Gemini Vision is reading your menu — usually 5–15 seconds. Each detected section becomes a new menu under that meal.
          </div>
        )}

        {/* Meal sections */}
        <div className="space-y-4">
          {MEAL_OPTIONS.map((meal) => {
            const list = variants[meal.key] || [];
            const atCap = list.length >= MAX_VARIANTS_PER_MEAL;
            return (
              <div key={meal.key} className={`border rounded ${TONE_BG[meal.tone]}`}
                data-testid={`meal-section-${meal.key}`}>
                <div className="flex items-center justify-between gap-3 px-3 py-2 border-b border-current/20">
                  <div>
                    <div className="text-sm font-bold uppercase tracking-wider">{meal.label}</div>
                    <div className="text-[10px] opacity-70">
                      {meal.hint} · {list.length}/{MAX_VARIANTS_PER_MEAL} menus
                    </div>
                  </div>
                </div>
                <div className="px-3 py-2 bg-white space-y-3">
                  {list.length === 0 && (
                    <p className="text-xs text-text-secondary italic px-1">No {meal.label.toLowerCase()} menus yet — add one below or scan a menu photo.</p>
                  )}
                  {list.map((v, vIdx) => (
                    <VariantCard
                      key={vIdx}
                      meal={meal.key}
                      vIdx={vIdx}
                      variant={v}
                      onChange={(next) => updateVariant(meal.key, vIdx, next)}
                      onDelete={() => deleteVariant(meal.key, vIdx)}
                    />
                  ))}
                  <Button
                    onClick={() => addVariant(meal.key)}
                    variant="outline"
                    size="sm"
                    disabled={atCap}
                    className="rounded-none border-dashed border-[#E8E1D5] w-full text-xs h-9 disabled:opacity-50"
                    data-testid={`add-variant-${meal.key}`}>
                    <Plus size={14} className="mr-1" />
                    {atCap ? `Maximum ${MAX_VARIANTS_PER_MEAL} ${meal.label} menus reached`
                      : `Add ${meal.label} menu`}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {totalVariants === 0 && (
          <p className="mt-3 text-[11px] text-amber-700 italic flex items-center gap-1">
            <Warning size={12} /> Add at least one menu so customers can book your catering.
          </p>
        )}

        <div className="mt-4 flex items-center justify-between gap-2">
          <p className="text-xs text-text-secondary">
            {totalVariants} menu{totalVariants === 1 ? '' : 's'} across all meals
          </p>
          <Button onClick={save} disabled={saving}
            className="rounded-none bg-brand-primary hover:bg-[#A84A38] text-sand-50 disabled:opacity-60"
            data-testid="menu-save-btn">
            <FloppyDisk size={16} className="mr-2" weight="fill" />
            {saving ? 'Saving…' : 'Save Menus'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Normalise a server variant payload into editable form (string-ish prices).
function normaliseVariant(v) {
  return {
    name: v?.name || '',
    price_per_plate: v?.price_per_plate != null ? String(v.price_per_plate) : '',
    dishes: Array.isArray(v?.dishes) ? v.dishes.map((d) => ({ name: d?.name || '' })) : [],
  };
}

export default CatererMenuCard;
